import { partnersQueries } from '@/api/partners.service';

import { queryClient } from '@/configs/queryClient';
import { PartnerDto } from '@/generated-types';

/**
 * Session Storage Cache utilities
 * All keys are formatted as: key_partnerId.
 * The public partner id is stable across draft-token refreshes and is not a credential.
 */
class SessionStorageCache {
	/**
	 * Generates a cache key in the format: key_partnerId
	 * @param key - The base key for the cache entry
	 * @returns Formatted cache key or null if no partner config
	 */
	private generateCacheKey(key: string): string | null {
		const partnerId = queryClient.getQueryData<Required<PartnerDto>>(
			partnersQueries.partner.me.queryKey,
		)?.id;
		if (!partnerId) {
			return null;
		}
		return `${key}_${partnerId}`;
	}

	/**
	 * Sets a value in session storage with the formatted key
	 * @param key - The base key for the cache entry
	 * @param value - The value to store (will be JSON stringified)
	 * @returns boolean indicating success
	 */
	set<T>(key: string, value: T): boolean {
		try {
			const cacheKey = this.generateCacheKey(key);
			if (!cacheKey) {
				console.warn('SessionStorageCache: No partner config available, cannot set cache');
				return false;
			}
			sessionStorage.setItem(cacheKey, JSON.stringify(value));
			return true;
		} catch (error) {
			console.error('SessionStorageCache: Error setting cache', error);
			return false;
		}
	}

	/**
	 * Gets a value from session storage using the formatted key
	 * @param key - The base key for the cache entry
	 * @returns The parsed value or null if not found/error
	 */
	get<T>(key: string): T | null {
		try {
			const cacheKey = this.generateCacheKey(key);
			if (!cacheKey) {
				return null;
			}
			const item = sessionStorage.getItem(cacheKey);
			if (!item) {
				return null;
			}
			return JSON.parse(item) as T;
		} catch (error) {
			console.error('SessionStorageCache: Error getting cache', error);
			return null;
		}
	}

	/**
	 * Removes a value from session storage using the formatted key
	 * @param key - The base key for the cache entry
	 * @returns boolean indicating success
	 */
	remove(key: string): boolean {
		try {
			const cacheKey = this.generateCacheKey(key);
			if (!cacheKey) {
				return false;
			}
			sessionStorage.removeItem(cacheKey);
			return true;
		} catch (error) {
			console.error('SessionStorageCache: Error removing cache', error);
			return false;
		}
	}

	/**
	 * Checks if a key exists in session storage
	 * @param key - The base key for the cache entry
	 * @returns boolean indicating if the key exists
	 */
	has(key: string): boolean {
		const cacheKey = this.generateCacheKey(key);
		if (!cacheKey) {
			return false;
		}
		return sessionStorage.getItem(cacheKey) !== null;
	}
}

export const sessionStorageCache = new SessionStorageCache();
